import { HttpApiService } from "@/shared/services/httpApi.service";

import { PAYMENTS_API_ENDPOINTS } from "../constants/payments.constants";
import type { NewPaymentFormFullData } from "../types/new-payment.types";
import type {
  CreatePaymentResponse,
  FetchPaymentHistoryParams,
  FetchPaymentsParams,
  MarkPaymentPaidInput,
  PaymentByIdPayload,
  PaymentHistoryListResponse,
  PaymentsListResponse,
  PaymentStatusUpdateApi,
  PendingCurrencyTotalsResponse,
} from "../types/payments-api.types";
import { buildFormDataForPayment } from "../utils/formDataHelpers";

export const paymentsService = {
  fetchById: async (id: string): Promise<PaymentByIdPayload> => {
    const path = PAYMENTS_API_ENDPOINTS.paymentById(id);
    const response = await HttpApiService.get<PaymentByIdPayload>(path);
    return response.data;
  },

  fetchList: async (params?: FetchPaymentsParams): Promise<PaymentsListResponse> => {
    const path = PAYMENTS_API_ENDPOINTS.PAYMENTS;
    const queryParams: string[] = [];
    if (params?.skip != null) queryParams.push(`skip=${params.skip}`);
    if (params?.limit != null) queryParams.push(`limit=${params.limit}`);
    const url = queryParams.length ? `${path}?${queryParams.join("&")}` : path;
    const response = await HttpApiService.get<PaymentsListResponse>(url);
    return response.data;
  },

  create: async (data: NewPaymentFormFullData): Promise<CreatePaymentResponse> => {
    const formData = buildFormDataForPayment(data);
    const response = await HttpApiService.post<CreatePaymentResponse, FormData>(
      PAYMENTS_API_ENDPOINTS.PAYMENTS,
      formData,
    );
    return response.data;
  },

  getAttachmentPresignedUrl: async (paymentId: string): Promise<string> => {
    const path = PAYMENTS_API_ENDPOINTS.attachmentPresignedUrl(paymentId);
    const response = await HttpApiService.get<{ url: string }>(path);
    return response.data.url;
  },

  cancel: async (paymentId: string, note: string): Promise<void> => {
    const path = PAYMENTS_API_ENDPOINTS.paymentById(paymentId);
    const url = `${path}?deletion_note=${encodeURIComponent(note)}`;
    await HttpApiService.delete<void>(url);
  },

  updateStatus: async (paymentId: string, status: PaymentStatusUpdateApi): Promise<void> => {
    const path = PAYMENTS_API_ENDPOINTS.status(paymentId);
    await HttpApiService.patch<void, { status: PaymentStatusUpdateApi }>(path, { status });
  },

  /**
   * Marks the payment as paid and stores its proof in a **single atomic call**: the receipts are uploaded
   * before the status changes, so a failure can never leave a payment paid without backing. Idempotent — a
   * retry does not duplicate receipts.
   */
  markAsPaid: async (
    paymentId: string,
    { paymentSource, urlHas, files }: MarkPaymentPaidInput,
  ): Promise<PaymentByIdPayload> => {
    const path = PAYMENTS_API_ENDPOINTS.pay(paymentId);
    const formData = new FormData();
    formData.append("payment_source", paymentSource.trim());
    if (urlHas?.trim()) formData.append("url_has", urlHas.trim());
    for (const file of files ?? []) {
      formData.append("attachments", file, file.name);
    }
    const response = await HttpApiService.post<PaymentByIdPayload, FormData>(path, formData);
    return response.data;
  },

  /**
   * Takes the payment and moves it to IN_PROGRESS in a single atomic backend call. Idempotent: retrying a
   * payment the caller already holds succeeds instead of failing.
   */
  take: async (paymentId: string): Promise<PaymentByIdPayload> => {
    const path = PAYMENTS_API_ENDPOINTS.take(paymentId);
    const response = await HttpApiService.post<PaymentByIdPayload, Record<string, never>>(path, {});
    return response.data;
  },

  /** Returns the payment to the queue: clears the lock and moves it back to PENDING. */
  release: async (paymentId: string): Promise<PaymentByIdPayload> => {
    const path = PAYMENTS_API_ENDPOINTS.release(paymentId);
    const response = await HttpApiService.post<PaymentByIdPayload, Record<string, never>>(path, {});
    return response.data;
  },

  /** Admin-only: releases a payment held by another user. */
  forceRelease: async (paymentId: string): Promise<PaymentByIdPayload> => {
    const path = PAYMENTS_API_ENDPOINTS.forceRelease(paymentId);
    const response = await HttpApiService.post<PaymentByIdPayload, Record<string, never>>(path, {});
    return response.data;
  },

  sendTakerReminder: async (paymentId: string): Promise<void> => {
    const path = PAYMENTS_API_ENDPOINTS.takerReminder(paymentId);
    await HttpApiService.post<unknown, Record<string, never>>(path, {});
  },

  fetchPendingTotalsByCurrency: async (): Promise<PendingCurrencyTotalsResponse> => {
    const response = await HttpApiService.get<PendingCurrencyTotalsResponse>(
      PAYMENTS_API_ENDPOINTS.pendingTotals,
    );
    return response.data;
  },

  fetchHistory: async (
    paymentId: string,
    params?: FetchPaymentHistoryParams,
  ): Promise<PaymentHistoryListResponse> => {
    const path = PAYMENTS_API_ENDPOINTS.history(paymentId);
    const queryParams: string[] = [];
    if (params?.skip != null) queryParams.push(`skip=${params.skip}`);
    if (params?.limit != null) queryParams.push(`limit=${params.limit}`);
    const url = queryParams.length ? `${path}?${queryParams.join("&")}` : path;
    const response = await HttpApiService.get<PaymentHistoryListResponse>(url);
    return response.data;
  },
};
